import React, { useState, useRef, useEffect } from 'react';
import { Eye, Download, Search, Upload, X, Check, ChevronDown, Plus, FileText, Heart, BookOpen, Newspaper, Users, Trash2, MessageSquare, FolderOpen } from 'lucide-react';
import { useAuth } from '../../../auth/AuthContext';
import { getApiUrl } from '../../../utils/apiConfig';
import { downloadReportPDF } from '../../../utils/generateReportPDF';
import * as XLSX from 'xlsx';
import './styles.css';

// ── Icons ──────────────────────────────────────────────
const ExportIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

// ── Helpers ────────────────────────────────────────────
function formatDate(raw) {
  if (!raw) return '—';
  const isoStr = typeof raw === 'string' ? raw.trim().replace(' ', 'T') : raw;
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return String(raw);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function Badge({ status }) {
  return <span className={`rp-badge ${status}`}>{status}</span>;
}

function Toast({ message, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 3000); return () => clearTimeout(t); }, [onDone]);
  return <div className="rp-toast"><Check size={14} /> {message}</div>;
}

// ── Field wrapper ──────────────────────────────────────
function Field({ label, required, hint, error, children }) {
  return (
    <div className="kf-field">
      <label className="kf-label">
        {label}{required && <span style={{ color: '#ef4444' }}> *</span>}
        {hint && <em className="kf-hint"> {hint}</em>}
      </label>
      {children}
      {error && <span className="kf-error">{error}</span>}
    </div>
  );
}

// ── Media uploader ─────────────────────────────────────
function MediaUploader({ files, onAdd, onRemove, label = "Upload Images / Files", accept = "image/*,video/*,.pdf" }) {
  const ref = useRef();
  return (
    <div>
      <label className="kf-dropzone" onClick={() => ref.current?.click()}>
        <input ref={ref} type="file" accept={accept} multiple style={{ display: 'none' }}
          onChange={e => { onAdd(Array.from(e.target.files)); e.target.value = ''; }} />
        <div className="kf-dropzone-inner">
          <Upload size={20} color="#c9a84c" />
          <span>{label}</span>
          <small>Click to browse files</small>
        </div>
      </label>
      {files.length > 0 && (
        <div className="rp-media-grid" style={{ marginTop: 12 }}>
          {files.map((f, i) => (
            <div className="rp-media-thumb" key={i}>
              {f.url && f.type?.startsWith('video') ? <video src={f.url} className="rp-media-img" muted />
                : f.url ? <img src={f.url} alt={f.name} className="rp-media-img" />
                  : <div className="rp-media-img rp-media-pdf-thumb">{f.name?.endsWith('.pdf') ? 'PDF' : 'DOC'}</div>}
              <button className="rp-media-remove" onClick={() => onRemove(i)}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Clarification Modal ───────────────────────────────
function ClarificationModal({ report, endpoint, onClose, onDone }) {
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const baseUrl = window.ENV?.API_PATH || process.env.REACT_APP_API_URL;

  const send = async () => {
    if (!note.trim()) return;
    setLoading(true);
    try {
      await fetch(`${baseUrl}${endpoint}/${report.id.replace(/^[A-Z]+-/, '')}/clarification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note }),
      });
      onDone();
    } catch (e) {
      console.error(e);
      alert('Failed to send clarification request');
    }
    setLoading(false);
  };

  return (
    <div className="rp-modal-overlay" onClick={onClose}>
      <div className="rp-modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="rp-modal-header">
          <h3>Ask for Clarification</h3>
          <button className="rp-remove-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="rp-modal-body">
          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
            Your note will be saved against this report and its status will be set to <strong>Needs Clarification</strong>.
          </p>
          <textarea
            rows={4}
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Type your clarification request here..."
            className="kf-textarea"
            style={{ width: '100%', fontSize: 13 }}
          />
        </div>
        <div className="rp-modal-footer">
          <button className="rp-ghost-btn" onClick={onClose}>Cancel</button>
          <button className="submit-report-btn" onClick={send} disabled={loading} style={{ '--btn-color': '#f59e0b' }}>
            {loading ? 'Sending…' : 'Send Request'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Report Table ───────────────────────────────────────
function ReportTable({ reports, loading, columns, onView, onDownload, onApprove, onClarify, onDelete, userRole }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="rp-table">
        <thead>
          <tr>
            {columns.map(c => <th key={c.key}>{c.label}</th>)}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={columns.length + 1} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>Loading…</td></tr>
          ) : reports.length === 0 ? (
            <tr><td colSpan={columns.length + 1} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No reports yet. Click the button above to submit one.</td></tr>
          ) : reports.map(row => (
            <tr key={row.id}>
              {columns.map(c => (
                <td key={c.key}>
                  {c.key === 'status' ? <Badge status={row[c.key]} />
                    : c.key === 'id' ? <span className="rp-id">{row[c.key]}</span>
                      : row[c.key] ?? '—'}
                </td>
              ))}
              <td>
                <div className="rp-actions">
                  <button className="rp-icon-btn view" title="View" onClick={() => onView(row)}><Eye size={14} /></button>
                  <button className="rp-icon-btn dl" title="Download" onClick={() => onDownload(row)}><Download size={14} /></button>

                  {userRole === 'admin' && (
                    <>
                      {row.status !== 'APPROVED' && row.status !== 'approved' && (
                        <button className="rp-icon-btn approve" title="Approve" onClick={() => onApprove(row)} style={{ color: '#16a34a', borderColor: '#bbf7d0' }}>
                          <Check size={14} />
                        </button>
                      )}
                      <button className="rp-icon-btn clarify" title="Clarify" onClick={() => onClarify(row)} style={{ color: '#a16207', borderColor: '#fde68a' }}>
                        <MessageSquare size={14} />
                      </button>
                      <button className="rp-icon-btn delete" title="Delete" onClick={() => onDelete(row)} style={{ color: '#dc2626', borderColor: '#fecaca' }}>
                        <Trash2 size={14} />
                      </button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const KEY_LABELS = {
  // Zonal Report / Zone Info
  zoneName: "Name of Zone",
  zonalPastor: "Zonal Pastor's Name",
  zonalManager: "Zonal Manager",
  partnershipRemittance: "Total Partnership Remittance",
  remittancePurpose: "Purpose for each remittance",
  trumpetsBlown: "Trumpets Blown this week",
  greatShouts: "Great Shouts made this week",
  newPartners: "New partners recruited",
  testimoniesSubmitted: "Testimonies submitted to the Department",
  healingTranslations: "Total number of outreaches done this week",
  healingOutreaches: "Healing outreaches held this week",
  healingPicturesVideos: "Pictures and videos from Healing outreaches submitted",
  pastoralAttendanceDirector: "Zonal Pastor attendance in Executive Minister's meeting",
  managerAttendanceDirector: "Zonal Manager attendance in Executive Minister's meeting",
  managerAttendanceStrategy: "Zonal Manager attendance in strategy meeting",
  testimonyClarificationConcern: "Testimony, Clarification or Concern",
  submittedByEmail: "Submitted By Email",
  participationPrayWithMe: "Total number of participation pray with me",
  totalRegistrationHslhs: "Total Registration for HSLHS",
  activePrayerCloud: "Total Number of Active Prayer Clouds",
  totalHerald: "Total Number of Heralds",
  totalHeralds: "Total Number of Heralds",
  activatedHealingCentre: "Total Number of Activate Healing Centres",
  ongoingProgramsReport: "Brief Report for Ongoing Programs",
  heraldConference: "Brief report for Herald Conference",
  totalPartnershipRemittance: "Total Partnership Remittance",
  newPartnersRecruited: "New Partners Recruited",
  zonalPastorExecutiveMinistersMeeting: "Zonal Pastor attendance in Executive Minister's meeting",
  zonalManagerExecutiveMinistersMeeting: "Zonal Manager attendance in Executive Minister's meeting",
  zonalManagerStrategyMeeting: "Zonal Manager attendance in strategy meeting",
  zonalManagerStrategyMeetingAttendance: "Zonal Manager attendance in strategy meeting",
  regionName: "Region Name",
  submittedBy: "Submitted By",



  // Testimonials
  testimony: "Share a testimony",
  testimoniesCount: "Testimonies received this week",
  prayWithMeTestimonies: "Pray With Me Testimonies",
  translationTestimonies: "Translation Testimonies",
  partnershipTestimonies: "Partnership Testimonies",
  salvationTestimonies: "Salvation Testimonies",
  healingTestimonies: "Healing Testimonies",
  othersTestimonies: "Others Testimonies",

  // Partnership fields
  zonalPartnership: "Zonal Partnership",
  zonalPartnershipDetails: "Zonal Partnership Details",
  groupsPartnership: "Group Partnership",
  churchesPartnership: "Church Partnership",
  cellPartnership: "Cell Partnership",
  sponsoredTeenspiration: "Teenspiration (300 espees) Sponsored",
  sponsoredKidspiration: "Kidspiration (300 espees) Sponsored",


  // Magazine Report
  adultCopies: "Adult Copies Ordered",
  adultLanguages: "Adult Language(s)",
  teensCopies: "Teens Copies Ordered",
  teensLanguages: "Teens Language(s)",
  kidsCopies: "Kids Copies Ordered",
  kidsLanguages: "Kids Language(s)",
  ordered: "Total Ordered Copies",
  language: "Languages",
  received: "Total Received Copies",
  receiptStatus: "Receipt Status",
  reason: "Not received reason",
  sponsoredCopies: "Sponsored Copies",
  monthlyMinimumOrder: "Monthly Minimum Magazine Order",
  monthlyCopiesOrdered: "Cumulative number of copies sponsored for the month",
  praiseReports: "Praise Reports",
  datesReceived: "Dates Received",
  outreachLocations: "Outreach Locations",

  // Outreach Report
  date: "Date of Outreach",
  category: "Outreach Category",
  locations: "Outreach Location(s)",
  story: "Outreach Story",
  images: "Outreach Images Count",
  magazinesUsed: "Magazines Used",
  peopleInvolved: "People Involved",
  totalAttendance: "Total Attendance",
  soulsSaved: "Souls Saved",
  outreachTestimonies: "Testimonies from the outreach(es)",
  followUpPlan: "Further plans for soul retention",
  adminNote: "Admin Clarification Note",
};

// ── View Modal ─────────────────────────────────────────
function ViewModal({ report, onClose }) {
  if (!report) return null;
  const skip = ['id', 'media', 'mediaFiles', 'rawDate', 'formData'];
  const entries = Object.entries(report).filter(([k, v]) => !skip.includes(k) && v !== undefined && v !== null && v !== '');
  return (
    <div className="rp-modal-overlay" onClick={onClose}>
      <div className="rp-modal" onClick={e => e.stopPropagation()}>
        <div className="rp-modal-header">
          <div>
            <h3>{report.id}</h3>
            {report.status && <Badge status={report.status} />}
          </div>
          <button className="rp-remove-btn" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="rp-modal-body">
          {entries.map(([k, v]) => (
            <div className="rp-view-row" key={k}>
              <span className="rp-view-label">{KEY_LABELS[k] || k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</span>
              <span className="rp-view-value">{String(v)}</span>
            </div>
          ))}
          {report.media?.length > 0 && (
            <div className="rp-view-row rp-view-row-col">
              <span className="rp-view-label">Media ({report.media.length} files)</span>
              <div className="rp-media-grid" style={{ marginTop: 8 }}>
                {report.media.map((f, i) => (
                  <div className="rp-media-thumb" key={i}>
                    {f.url && f.type?.startsWith('video') ? <video src={f.url} className="rp-media-img" controls muted />
                      : f.url ? <img src={f.url} alt={f.name} className="rp-media-img" />
                        : <div className="rp-media-img rp-media-pdf-thumb">PDF</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="rp-modal-footer">
          <button className="rp-ghost-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════
// FORM WRAPPER — used by all forms
// ══════════════════════════════════════════════════════
function FormContainer({ title, eyebrow, icon, onSubmit, submitting, submitLabel, children }) {
  return (
    <div className="inline-form-container" style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
      <div className="popup-header" style={{ borderBottom: '1px solid #f1f5f9' }}>
        <div className="popup-title-wrap">
          <span className="popup-eyebrow">{eyebrow}</span>
          <div className="popup-title">
            <span className="popup-title-icon">{icon}</span>
            <span>{title}</span>
          </div>
        </div>
      </div>
      <div className="popup-scroll" style={{ maxHeight: 'none' }}>
        {children}
      </div>
      <div className="popup-footer" style={{ borderTop: '1px solid #f1f5f9' }}>
        <div style={{flex: 1}}></div>
        <button className="popup-submit-btn" onClick={onSubmit} disabled={submitting}>
          {submitting ? (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ animation: 'spin 0.8s linear infinite' }}>
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              Submitting…
            </>
          ) : (
            <><Check size={15} /> {submitLabel}</>
          )}
        </button>
      </div>
    </div>
  );
}

const spinStyle = document.createElement('style');
spinStyle.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
document.head.appendChild(spinStyle);


// ══════════════════════════════════════════════════════
// ZONAL INFORMATION FORM
// ══════════════════════════════════════════════════════
function ZonalInformationForm({ onClose, onSubmit: parentSubmit }) {
  const { user } = useAuth();
  const EMPTY = {
    zoneName: '',
    zonalPastor: '',
    zonalManager: '',
    pastoralAttendanceDirector: '',
    managerAttendanceDirector: '',
    managerAttendanceStrategy: '',
    testimonyClarificationConcern: '',
  };
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const ATTENDANCE_OPTIONS = ['', 'Yes', 'No', 'Officially Excused'];

  const validate = () => {
    const e = {};
    if (!form.zoneName.trim()) e.zoneName = 'Required';
    if (!form.zonalPastor.trim()) e.zonalPastor = 'Required';
    if (!form.zonalManager.trim()) e.zonalManager = 'Required';
    if (!form.pastoralAttendanceDirector) e.pastoralAttendanceDirector = 'Required';
    if (!form.managerAttendanceDirector) e.managerAttendanceDirector = 'Required';
    if (!form.managerAttendanceStrategy) e.managerAttendanceStrategy = 'Required';
    return e;
  };

  const handleSubmit = async () => {
    const e = validate(); setErrors(e);
    if (Object.keys(e).length) return;
    setSubmitting(true);
    const today = new Date().toISOString().split('T')[0];
    const payload = {
      submittedBy: form.zonalManager || user?.name || user?.displayName || 'User',
      submitterEmail: user?.email || 'admin@loveworld.com',
      submittedDate: today,
      submittedTime: new Date().toTimeString().split(' ')[0],
      weekStartDate: today,
      zoneName: form.zoneName || 'Default Zone',
      zonalPastor: form.zonalPastor || '—',
      zonalManager: form.zonalManager || user?.name || 'User',
      zonalPastorExecutiveMinistersMeeting: form.pastoralAttendanceDirector || 'No',
      zonalManagerExecutiveMinistersMeeting: form.managerAttendanceDirector || 'No',
      zonalManagerStrategyMeeting: form.managerAttendanceStrategy || 'No',
      testimonyClarificationConcern: form.testimonyClarificationConcern || '',
      regionName: user?.region || 'Global',
    };
    try {
      const res = await fetch(`${getApiUrl()}/api/reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Server returned ${res.status}: ${txt}`);
      }
      const saved = await res.json();
      parentSubmit({
        ...saved,
        zoneName: form.zoneName,
        zonalPastor: form.zonalPastor,
        zonalManager: form.zonalManager,
        status: 'PENDING',
        rawDate: today,
      });
    } catch (err) {
      console.error('Failed to save zonal information to DB', err);
      alert(`Submission failed: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormContainer title="Zonal Information" eyebrow="KingsForms · Zonal Info" icon="🏛️"
      onSubmit={handleSubmit} submitting={submitting} submitLabel="Submit Zonal Information">

      {/* Zone Info */}
      <div className="popup-section-head">🏛️ Zone Information</div>
      <div className="popup-fields">
        <Field label="Name of Zone" required error={errors.zoneName}>
          <input className={`kf-input${errors.zoneName ? ' kf-input-err' : ''}`} placeholder="E.g. Zone A"
            value={form.zoneName} onChange={e => set('zoneName', e.target.value)} />
        </Field>
        <Field label="Zonal Pastor's Name" required error={errors.zonalPastor}>
          <input className={`kf-input${errors.zonalPastor ? ' kf-input-err' : ''}`} placeholder="Full Name"
            value={form.zonalPastor} onChange={e => set('zonalPastor', e.target.value)} />
        </Field>
        <Field label="Zonal Manager" required error={errors.zonalManager}>
          <input className={`kf-input${errors.zonalManager ? ' kf-input-err' : ''}`} placeholder="Full Name"
            value={form.zonalManager} onChange={e => set('zonalManager', e.target.value)} />
        </Field>
      </div>

      {/* Attendance & Sponsorship */}
      <div className="popup-section-head">📅 Attendance and Meetings</div>
      <div className="popup-fields">
        <Field label="Zonal Pastor's Attendance in the Executive Minister's Weekly Meeting?" required error={errors.pastoralAttendanceDirector}>
          <div className="kf-select-wrap">
            <select className={`kf-select${errors.pastoralAttendanceDirector ? ' kf-input-err' : ''}`} value={form.pastoralAttendanceDirector} onChange={e => set('pastoralAttendanceDirector', e.target.value)}>
              <option value="">Please Select</option>
              {ATTENDANCE_OPTIONS.filter(o => o).map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
            <ChevronDown size={13} className="kf-select-chevron" />
          </div>
        </Field>
        <Field label="Zonal Manager's Attendance in the Executive Minister's Weekly Meeting?" required error={errors.managerAttendanceDirector}>
          <div className="kf-select-wrap">
            <select className={`kf-select${errors.managerAttendanceDirector ? ' kf-input-err' : ''}`} value={form.managerAttendanceDirector} onChange={e => set('managerAttendanceDirector', e.target.value)}>
              <option value="">Please Select</option>
              {ATTENDANCE_OPTIONS.filter(o => o).map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
            <ChevronDown size={13} className="kf-select-chevron" />
          </div>
        </Field>
        <Field label="Zonal Manager's Attendance in the Weekly Managers Strategy Meeting?" required error={errors.managerAttendanceStrategy}>
          <div className="kf-select-wrap">
            <select className={`kf-select${errors.managerAttendanceStrategy ? ' kf-input-err' : ''}`} value={form.managerAttendanceStrategy} onChange={e => set('managerAttendanceStrategy', e.target.value)}>
              <option value="">Please Select</option>
              {ATTENDANCE_OPTIONS.filter(o => o).map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
            <ChevronDown size={13} className="kf-select-chevron" />
          </div>
        </Field>
        <Field label="Any Testimony, Clarification or Concern?">
          <textarea className="kf-textarea" rows={3} placeholder="Provide Details..."
            value={form.testimonyClarificationConcern} onChange={e => set('testimonyClarificationConcern', e.target.value)} />
        </Field>
      </div>
    </FormContainer>
  );
}


// ══════════════════════════════════════════════════════
// ZONAL REPORT FORM — matched to kingsforms.online fields
// ══════════════════════════════════════════════════════
function ZonalReportForm({ onClose, onSubmit: parentSubmit }) {
  const { user } = useAuth();
  const EMPTY = {
    zoneName: '',
    zonalManager: '',
    partnershipRemittance: '',
    popMedia: [],
    popMediaFiles: [],
    remittancePurpose: '',
    trumpetsBlown: '',
    greatShouts: '',
    newPartners: '',
    testimoniesSubmitted: '',
    healingTranslations: '',
    healingOutreaches: '',
    healingPicturesVideos: '',
    pastoralAttendanceDirector: '',
    managerAttendanceDirector: '',
    managerAttendanceStrategy: '',
    testimonyClarificationConcern: '',
    media: [],
    mediaFiles: [],
    totalRegistrationHslhs: '',
    activePrayerCloud: '',
    totalHerald: '',
    activatedHealingCentre: '',
    ongoingProgramsReport: '',
    reportDate: '',
  };
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const validate = () => {
    const e = {};
    return e;
  };

  const handleSubmit = async () => {
    const e = validate(); setErrors(e);
    if (Object.keys(e).length) return;
    setSubmitting(true);
    const today = new Date().toISOString().split('T')[0];
    const payload = {
      // Fields matching CreateReportRequest / zone_weekly_reports table
      submittedBy: user?.displayName || user?.name || 'User',
      submitterEmail: user?.email || 'admin@loveworld.com',
      submittedDate: today,
      submittedTime: new Date().toTimeString().split(' ')[0],
      weekStartDate: form.reportDate || today,
      zoneName: user?.zone || user?.region || 'Default Zone',
      zonalManager: user?.displayName || user?.name || 'Zonal Manager',
      zonalPastor: '—',
      zonalPastorExecutiveMinistersMeeting: 'No',
      zonalManagerExecutiveMinistersMeeting: 'No',
      zonalManagerStrategyMeeting: 'No',
      popMediaUrl: null,
      regionName: user?.region || 'Global',
      totalRegistrationHslhs: Number(form.totalRegistrationHslhs) || 0,
      activePrayerCloud: Number(form.activePrayerCloud) || 0,
      totalHerald: Number(form.totalHerald) || 0,
      activatedHealingCentre: Number(form.activatedHealingCentre) || 0,
      ongoingProgramsReport: form.ongoingProgramsReport || '',
    };
    try {
      const res = await fetch(`${getApiUrl()}/api/reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Server returned ${res.status}: ${txt}`);
      }
      const saved = await res.json();
      parentSubmit({
        ...saved,
        submittedBy: payload.submittedBy,
        partners: form.newPartners,
        remittance: form.partnershipRemittance,
        status: 'PENDING',
        media: form.media,
        rawDate: today,
      });
    } catch (err) {
      console.error('Failed to save zonal report to DB', err);
      alert(`Submission failed: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormContainer title="Weekly Programs/Campaign Engagement Report" eyebrow="KingsForms · Programs/Campaign Engagement Report" icon="🏛️"
      onSubmit={handleSubmit} submitting={submitting} submitLabel="Submit Programs/Campaign Engagement Report">

      <div className="popup-section-head">📣 Programs & Campaigns</div>
      <div className="popup-fields">
        <Field label="Date" required error={errors.reportDate}>
          <input className={`kf-input${errors.reportDate ? ' kf-input-err' : ''}`} type="date"
            value={form.reportDate} onChange={e => set('reportDate', e.target.value)} />
        </Field>
        <Field label="Total Registration for HSLHS">
          <input className="kf-input" type="number" min="0" placeholder="0"
            value={form.totalRegistrationHslhs} onChange={e => set('totalRegistrationHslhs', e.target.value)} />
        </Field>
        <Field label="Total Number of Active Prayer Clouds">
          <input className="kf-input" type="number" min="0" placeholder="0"
            value={form.activePrayerCloud} onChange={e => set('activePrayerCloud', e.target.value)} />
        </Field>
        <Field label="Total Number of Heralds">
          <input className="kf-input" type="number" min="0" placeholder="0"
            value={form.totalHerald} onChange={e => set('totalHerald', e.target.value)} />
        </Field>
        <Field label="Total Number of Activate Healing Centres">
          <input className="kf-input" type="number" min="0" placeholder="0"
            value={form.activatedHealingCentre} onChange={e => set('activatedHealingCentre', e.target.value)} />
        </Field>
        <Field label="Brief Report for Ongoing Programs (Eg, Herald Conference etc)">
          <textarea className="kf-textarea" rows={3} placeholder="Enter details..."
            value={form.ongoingProgramsReport} onChange={e => set('ongoingProgramsReport', e.target.value)} />
        </Field>
      </div>

      {/* Media */}
      <div className="popup-section-head">📎 Supporting Media</div>
      <div className="popup-fields">
        <MediaUploader files={form.media}
          onAdd={files => {
            const prev = files.map(f => ({ name: f.name, type: f.type, url: (f.type.startsWith('image') || f.type.startsWith('video')) ? URL.createObjectURL(f) : null, size: f.size }));
            set('media', [...form.media, ...prev]); set('mediaFiles', [...form.mediaFiles, ...files]);
          }}
          onRemove={i => { set('media', form.media.filter((_, j) => j !== i)); set('mediaFiles', form.mediaFiles.filter((_, j) => j !== i)); }}
        />
      </div>
    </FormContainer>
  );
}


// ══════════════════════════════════════════════════════
// PARTNERSHIP REPORT FORM
// ══════════════════════════════════════════════════════


const BLAAAST_CATEGORIES = [
  { key: 'joyfulSound', label: 'Make a Joyful Sound', amount: '30 Espees' },
  { key: 'raiseBanner', label: 'Raise a Banner', amount: '300 Espees' },
  { key: 'takeShield', label: 'Take Up the Shield', amount: '1,000 Espees' },
  { key: 'greatShout', label: 'Make a Great Shout', amount: '2,500 Espees' },
  { key: 'advanceTroop', label: 'Advance Against a Troop', amount: '5,000 Espees' },
  { key: 'blowTrumpet', label: 'Blow A Trumpet', amount: '10,000 Espees' },
  { key: 'liftHorn', label: 'Lift the Horn', amount: '100,000 Espees' },
  { key: 'blastVictory', label: 'Blast of Victory (Gold Sponsorship)', amount: '250,000 Espees' },
];

function PartnershipForm({ onClose, onSubmit: parentSubmit }) {
  const [form, setForm] = useState({
    reportDate: '',
    zonalPartnership: '',
    zonalPartnershipDetails: '',
    groupsPartnership: '',
    churchesPartnership: '',
    cellPartnership: '',
    blaaast: {},
    notes: '',
    others: '',
    milestones: [{ pastorName: '', groupName: '', milestoneName: '' }],
    sponsoredTeenspiration: '',
    sponsoredKidspiration: '',
    popMedia: [],
    popMediaFiles: [],
    trumpetsBlown: '',
    greatShouts: '',
    testimoniesSubmitted: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const setBlaaast = (key, val) => {
    const int = val === '' ? '' : String(Math.max(0, Math.floor(Number(val))));
    setForm(p => ({ ...p, blaaast: { ...p.blaaast, [key]: int } }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    await new Promise(r => setTimeout(r, 500));

    const finalNotes = form.others || '';

    parentSubmit({
      zonalPartnership: form.zonalPartnership,
      zonalPartnershipDetails: form.zonalPartnershipDetails,
      groupsPartnership: form.groupsPartnership,
      churchesPartnership: form.churchesPartnership,
      cellPartnership: form.cellPartnership,
      totalRemittance: Number(form.totalRemittance) || 0,
      healingCrusadeSponsorship: 0.00,
      newPartnersRecruited: Number(form.newPartnersRecruited) || 0,
      remittancePurpose: form.remittancePurpose,
      trumpetsBlown: Number(form.trumpetsBlown) || 0,
      greatShouts: Number(form.greatShouts) || 0,
      testimoniesSubmitted: Number(form.testimoniesSubmitted) || 0,
      notes: finalNotes,
      status: 'submitted',
      rawDate: form.reportDate || new Date().toISOString().split('T')[0],
      groupPastorsMilestones: form.milestones
        .filter(m => m.pastorName || m.groupName || m.milestoneName)
        .map((m, i) => `Entry ${i + 1} — Pastor: ${m.pastorName}, Group: ${m.groupName}, Milestone: ${m.milestoneName}`)
        .join('\n'),
      sponsoredTeenspiration: form.sponsoredTeenspiration,
      sponsoredKidspiration: form.sponsoredKidspiration,
      popMedia: form.popMedia
    });
    setSubmitting(false);
  };

  return (
    <FormContainer title="Weekly Partnership Report" eyebrow="KingsForms · Partnership" icon="🤝"
      onSubmit={handleSubmit} submitting={submitting} submitLabel="Submit Partnership Report">

      <div className="popup-section-head">💼 Partnership Breakdown (Espees)</div>
      <div className="popup-fields">
        <Field label="Date" required>
          <input className="kf-input" type="date"
            value={form.reportDate} onChange={e => setForm(p => ({ ...p, reportDate: e.target.value }))} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1rem', alignItems: 'start' }}>
          <Field label="Zonal Partnership for this Week">
            <input className="kf-input" type="text" placeholder="Enter Zonal Partnership (Eg, 5000 Espees)..."
              value={form.zonalPartnership} onChange={e => setForm(p => ({ ...p, zonalPartnership: e.target.value }))} />
          </Field>
          <Field label="Zonal Partnership Details (Kindly State How Much Was Given for Each Arm Eg HSLHS, HTTNM, Crusade etc)">
            <textarea className="kf-textarea" rows={1} placeholder="Enter Details..."
              value={form.zonalPartnershipDetails} onChange={e => setForm(p => ({ ...p, zonalPartnershipDetails: e.target.value }))} />
          </Field>
        </div>

        <Field label="Proof of Payment (POP)" hint="Upload POP for the Transactions">
          <MediaUploader files={form.popMedia}
            onAdd={files => {
              const prev = files.map(f => ({ name: f.name, type: f.type, url: (f.type.startsWith('image') || f.type.startsWith('video')) ? URL.createObjectURL(f) : null, size: f.size }));
              setForm(p => ({ ...p, popMedia: [...p.popMedia, ...prev], popMediaFiles: [...p.popMediaFiles, ...files] }));
            }}
            onRemove={i => { setForm(p => ({ ...p, popMedia: p.popMedia.filter((_, j) => j !== i), popMediaFiles: p.popMediaFiles.filter((_, j) => j !== i) })); }}
            label="Upload POP"
          />
        </Field>

        <Field label="How Many Testimonies Were Submitted to the Department?">
          <input className="kf-input" type="number" min="0" placeholder="0"
            value={form.testimoniesSubmitted} onChange={e => setForm(p => ({ ...p, testimoniesSubmitted: e.target.value }))} />
        </Field>

        <Field label="How Many New Partners Were Recruited?">
          <input className="kf-input" type="number" min="0" placeholder="0"
            value={form.newPartnersRecruited} onChange={e => setForm(p => ({ ...p, newPartnersRecruited: e.target.value }))} />
        </Field>

        <Field label="Group Partnership (State How Much Was Remitted by Each Group)">
          <input className="kf-input" type="text" placeholder="Enter Group Partnership..."
            value={form.groupsPartnership} onChange={e => setForm(p => ({ ...p, groupsPartnership: e.target.value }))} />
        </Field>

        <Field label="Church Partnership (State How Much Was Remitted by Each Church)">
          <input className="kf-input" type="text" placeholder="Enter Church Partnership..."
            value={form.churchesPartnership} onChange={e => setForm(p => ({ ...p, churchesPartnership: e.target.value }))} />
        </Field>

        <Field label="Cell Partnership (State How Much Was Remitted by Each Cell)">
          <input className="kf-input" type="text" placeholder="Enter Cell Partnership..."
            value={form.cellPartnership} onChange={e => setForm(p => ({ ...p, cellPartnership: e.target.value }))} />
        </Field>
      </div>

      <div className="popup-section-head">🎺 BLAAAST Partnership Categories</div>
      <div className="popup-fields">
        <p className="kf-section-note">How Many Partners Gave in Each of These BLAAAST Categories This Week?</p>
        <div className="blaaast-grid">
          {BLAAAST_CATEGORIES.map(cat => (
            <div key={cat.key} className="blaaast-row">
              <div className="blaaast-info">
                <span className="blaaast-label">{cat.label}</span>
                <span className="blaaast-amount">{cat.amount}</span>
              </div>
              <input
                className="kf-input blaaast-input"
                type="number"
                min="0"
                step="1"
                placeholder="0"
                value={form.blaaast[cat.key] || ''}
                onChange={e => setBlaaast(cat.key, e.target.value)}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="popup-section-head">🏆 Group Milestones</div>
      <div className="popup-fields">
        {form.milestones.map((entry, idx) => (
          <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '1rem', alignItems: 'end', marginBottom: idx < form.milestones.length - 1 ? 8 : 0 }}>
            <Field label={idx === 0 ? 'State the Name of the Group Pastors' : ''}>
              <input className="kf-input" type="text" placeholder="Pastor Name"
                value={entry.pastorName} onChange={e => {
                  const updated = [...form.milestones];
                  updated[idx] = { ...updated[idx], pastorName: e.target.value };
                  setForm(p => ({ ...p, milestones: updated }));
                }} />
            </Field>
            <Field label={idx === 0 ? 'State the Name of Group' : ''}>
              <input className="kf-input" type="text" placeholder="Group Name"
                value={entry.groupName} onChange={e => {
                  const updated = [...form.milestones];
                  updated[idx] = { ...updated[idx], groupName: e.target.value };
                  setForm(p => ({ ...p, milestones: updated }));
                }} />
            </Field>
            <Field label={idx === 0 ? 'State the Name of Group Milestone' : ''}>
              <input className="kf-input" type="text" placeholder="Milestone Name"
                value={entry.milestoneName} onChange={e => {
                  const updated = [...form.milestones];
                  updated[idx] = { ...updated[idx], milestoneName: e.target.value };
                  setForm(p => ({ ...p, milestones: updated }));
                }} />
            </Field>
            <div style={{ paddingBottom: 4 }}>
              {form.milestones.length > 1 && (
                <button type="button" onClick={() => setForm(p => ({ ...p, milestones: p.milestones.filter((_, i) => i !== idx) }))}
                  style={{ background: 'none', border: '1px solid #fecaca', borderRadius: 6, color: '#dc2626', cursor: 'pointer', padding: '6px 8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  title="Remove Entry">
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
        ))}
        <button type="button"
          onClick={() => setForm(p => ({ ...p, milestones: [...p.milestones, { pastorName: '', groupName: '', milestoneName: '' }] }))}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: '1px dashed #c9a84c', borderRadius: 6, color: '#c9a84c', cursor: 'pointer', padding: '8px 16px', fontSize: 13, fontWeight: 600, marginTop: 4 }}>
          <Plus size={14} /> Add Another Entry
        </button>
        <Field label="Pastors and Members That Have Sponsored Teenspiration (300 Espees) This Week">
          <textarea className="kf-textarea" rows={2} placeholder="Enter Names and Amount"
            value={form.sponsoredTeenspiration} onChange={e => setForm(p => ({ ...p, sponsoredTeenspiration: e.target.value }))} />
        </Field>
        <Field label="Pastors and Members That Have Sponsored Kidspiration (300 Espees) This Week">
          <textarea className="kf-textarea" rows={2} placeholder="Enter Names and Amount"
            value={form.sponsoredKidspiration} onChange={e => setForm(p => ({ ...p, sponsoredKidspiration: e.target.value }))} />
        </Field>
      </div>

      <div className="popup-fields">
        <Field label="Additional Notes">
          <textarea className="kf-textarea" rows={2} placeholder="Enter Other Remarks or Comments..."
            value={form.others} onChange={e => setForm(p => ({ ...p, others: e.target.value }))} />
        </Field>
      </div>
    </FormContainer>
  );
}


// ══════════════════════════════════════════════════════
// TESTIMONIALS FORM
// ══════════════════════════════════════════════════════
const TESTIMONY_CATEGORIES = [
  { key: 'Healing Streams', label: 'Healing Streams' },
  { key: 'Partnership', label: 'Partnership' },
  { key: 'Healing to the Nations Magazines', label: 'Healing to the Nations Magazines' },
  { key: 'Prayer Clouds', label: 'Prayer Clouds' },
  { key: 'Crusades', label: 'Crusades' },
  { key: 'Pray With Me', label: 'Pray With Me' },
  { key: 'Heralds', label: 'Heralds' },
  { key: 'Others', label: 'Others' }
];

function TestimonialsForm({ onClose, onSubmit: parentSubmit }) {
  const [category, setCategory] = useState('');
  const [othersDetails, setOthersDetails] = useState('');
  const [documents, setDocuments] = useState([]);
  const [videos, setVideos] = useState([]);
  const [beforeImages, setBeforeImages] = useState([]);
  const [afterImages, setAfterImages] = useState([]);
  const [memberName, setMemberName] = useState('');
  const [categoryDetails, setCategoryDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleAddFiles = (setter) => (files) => {
    const prev = files.map(f => ({
      name: f.name, type: f.type,
      url: (f.type.startsWith('image') || f.type.startsWith('video')) ? URL.createObjectURL(f) : null,
      size: f.size,
    }));
    setter(p => [...p, ...prev]);
  };

  const handleRemoveFile = (setter) => (idx) => {
    setter(p => p.filter((_, i) => i !== idx));
  };

  const formatTestimonyContent = () => {
    let parts = [];
    parts.push(`Category: ${category}`);
    if (memberName.trim()) {
      parts.push(`Member Name: ${memberName}`);
    }
    if (categoryDetails.trim()) {
      parts.push(`Testimony Details:\n${categoryDetails}`);
    }

    const filesList = [];
    if (documents.length > 0) {
      filesList.push(`Documents (${documents.length}): ${documents.map(f => f.name).join(', ')}`);
    }
    if (videos.length > 0) {
      filesList.push(`Videos (${videos.length}): ${videos.map(f => f.name).join(', ')}`);
    }
    if (beforeImages.length > 0) {
      filesList.push(`Before Images (${beforeImages.length}): ${beforeImages.map(f => f.name).join(', ')}`);
    }
    if (afterImages.length > 0) {
      filesList.push(`After Images (${afterImages.length}): ${afterImages.map(f => f.name).join(', ')}`);
    }

    if (filesList.length > 0) {
      parts.push(`Attachments:\n- ` + filesList.join('\n- '));
    }

    return parts.join('\n\n');
  };

  const handleSubmit = async () => {
    if (!category) {
      setError('Please select a category.');
      return;
    }
    setError('');
    setSubmitting(true);
    await new Promise(r => setTimeout(r, 500));

    parentSubmit({
      testimony: '(Single testimony submitted)',
      testimoniesCount: 1, // Only one testimony
      memberName: memberName,
      categoryDetails: categoryDetails,
      beforeImages: beforeImages.length,
      afterImages: afterImages.length,
      documents: documents.length + videos.length,
      status: 'PENDING',
      rawDate: new Date().toISOString().split('T')[0],
      // We send it under the "othersTestimonies" field since it's dynamic now, or we can map it based on category. 
      // The backend saves these as text fields. We'll map it safely to "othersTestimonies" or just let the backend handle it.
      // Wait, let's just map it to the closest match or others.
      prayWithMeTestimonies: category === 'Pray With Me' ? formatTestimonyContent() : '',
      translationTestimonies: category === 'Heralds' ? formatTestimonyContent() : '',
      partnershipTestimonies: category === 'Partnership' ? formatTestimonyContent() : '',
      salvationTestimonies: category === 'Crusades' ? formatTestimonyContent() : '',
      healingTestimonies: (category === 'Healing Streams' || category === 'Healing to the Nations Magazines') ? formatTestimonyContent() : '',
      othersTestimonies: (category === 'Others' || category === 'Prayer Clouds') ? formatTestimonyContent() : '',
      othersDetails: category === 'Others' ? othersDetails : '',
    });
    setSubmitting(false);
  };

  return (
    <FormContainer title="Weekly Testimonials" eyebrow="KingsForms · Testimonies" icon="✍️"
      onSubmit={handleSubmit} submitting={submitting} submitLabel="Submit Testimony">

      {/* Category Dropdown */}
      <div className="popup-section-head">🏷️ Category</div>
      <div className="popup-fields">
        <Field label="Select a Category" error={error}>
          <select className="kf-input" value={category} onChange={e => { setCategory(e.target.value); if (e.target.value !== 'Others') setOthersDetails(''); }}>
            <option value="">Select a Category</option>
            {TESTIMONY_CATEGORIES.map(cat => (
              <option key={cat.key} value={cat.key}>{cat.label}</option>
            ))}
          </select>
        </Field>
        {category === 'Others' && (
          <Field label="Others Details (Please Write the Entire Testimony Here)">
            <textarea className="kf-textarea" rows={6} placeholder="Write Others Testimony Here..."
              value={othersDetails} onChange={e => setOthersDetails(e.target.value)} />
          </Field>
        )}
      </div>

      {category && (
        <div style={{ padding: '20px 32px' }}>
          <div className="popup-fields" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            <Field label="Member Name" hint="Who Does This Testimony Belong To?">
              <input className="kf-input" type="text" placeholder="E.g. Sister Anne"
                value={memberName} onChange={e => setMemberName(e.target.value)} />
            </Field>

            {category !== 'Others' && (
              <Field label={`${category} Details (Please Write the Entire Testimony Here)`}>
                <textarea className="kf-textarea" rows={6} placeholder={`Write ${category} Testimony Here...`}
                  value={categoryDetails} onChange={e => setCategoryDetails(e.target.value)} />
              </Field>
            )}

            {/* Uploads */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <Field label="Upload PDF or Word Files">
                <MediaUploader
                  files={documents}
                  onAdd={handleAddFiles(setDocuments)}
                  onRemove={handleRemoveFile(setDocuments)}
                  label="Upload PDF / Word"
                  accept=".pdf,.doc,.docx"
                />
              </Field>

              <Field label="Upload Video Testimony">
                <MediaUploader
                  files={videos}
                  onAdd={handleAddFiles(setVideos)}
                  onRemove={handleRemoveFile(setVideos)}
                  label="Upload Video"
                  accept="video/*"
                />
              </Field>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '8px' }}>
              <Field label="Before Images">
                <MediaUploader
                  files={beforeImages}
                  onAdd={handleAddFiles(setBeforeImages)}
                  onRemove={handleRemoveFile(setBeforeImages)}
                  label="Upload Before Images"
                  accept="image/*"
                />
              </Field>

              <Field label="After Images">
                <MediaUploader
                  files={afterImages}
                  onAdd={handleAddFiles(setAfterImages)}
                  onRemove={handleRemoveFile(setAfterImages)}
                  label="Upload After Images"
                  accept="image/*"
                />
              </Field>
            </div>
          </div>
        </div>
      )}
    </FormContainer>
  );
}


// ══════════════════════════════════════════════════════
// MAGAZINE FORM
// ══════════════════════════════════════════════════════


function MagazineForm({ onClose, onSubmit: parentSubmit }) {
  const [form, setForm] = useState({
    adultCopies: '',
    adultLanguages: '',
    adultLanguagesOther: '',
    teensCopies: '',
    teensLanguages: '',
    teensLanguagesOther: '',
    kidsCopies: '',
    kidsLanguages: '',
    kidsLanguagesOther: '',
    receivedCopies: '',
    notReceivedReason: '',
    challengesFaced: '',
    praiseReports: '',
    monthlyMinimumOrder: '',
    monthlyMinimumOrderAmountPaid: '',
    monthlyCopiesOrdered: '',
    cumulativeSponsoredAmountPaid: '',
    sponsorshipDate: '',
    amountPaidThisWeek: '',
    proofOfPaymentFiles: [],
    datesReceived: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    const e = {};
    const totalOrdered = (Number(form.adultCopies) || 0) + (Number(form.teensCopies) || 0) + (Number(form.kidsCopies) || 0);

    if (totalOrdered <= 0) {
      e.adultCopies = 'At least one magazine type must have copies ordered';
    }
    if (Number(form.adultCopies) > 0 && !form.adultLanguages.trim()) {
      e.adultLanguages = 'Required when copies are specified';
    }
    if (Number(form.teensCopies) > 0 && !form.teensLanguages.trim()) {
      e.teensLanguages = 'Required when copies are specified';
    }
    if (Number(form.kidsCopies) > 0 && !form.kidsLanguages.trim()) {
      e.kidsLanguages = 'Required when copies are specified';
    }

    if (!String(form.receivedCopies).trim()) e.receivedCopies = 'Required';
    if (Number(form.receivedCopies) < totalOrdered && !form.notReceivedReason.trim()) {
      e.notReceivedReason = 'Please provide a reason';
    }
    return e;
  };

  const handleSubmit = async () => {
    const e = validate(); setErrors(e);
    if (Object.keys(e).length) return;
    setSubmitting(true);
    await new Promise(r => setTimeout(r, 500));

    const totalOrdered = (Number(form.adultCopies) || 0) + (Number(form.teensCopies) || 0) + (Number(form.kidsCopies) || 0);
    const getLang = (lang, other) => lang === 'Other' ? (other || 'Other') : lang;
    const combinedLanguages = [
      form.adultLanguages && `Adult: ${getLang(form.adultLanguages, form.adultLanguagesOther)}`,
      form.teensLanguages && `Teens: ${getLang(form.teensLanguages, form.teensLanguagesOther)}`,
      form.kidsLanguages && `Kids: ${getLang(form.kidsLanguages, form.kidsLanguagesOther)}`
    ].filter(Boolean).join('; ');

    parentSubmit({
      adultCopies: Number(form.adultCopies) || 0,
      adultLanguages: form.adultLanguages,
      teensCopies: Number(form.teensCopies) || 0,
      teensLanguages: form.teensLanguages,
      kidsCopies: Number(form.kidsCopies) || 0,
      kidsLanguages: form.kidsLanguages,
      ordered: totalOrdered,
      language: combinedLanguages,
      received: Number(form.receivedCopies) || 0,
      receiptStatus: Number(form.receivedCopies) === 0 ? 'No' : (Number(form.receivedCopies) < totalOrdered ? 'Partial' : 'Yes'),
      reason: form.notReceivedReason,
      challengesFaced: form.challengesFaced,
      isAdult: (Number(form.adultCopies) || 0) > 0,
      isTeevolution: (Number(form.teensCopies) || 0) > 0,
      isKidsMagazine: (Number(form.kidsCopies) || 0) > 0,
      status: 'PENDING',
      rawDate: new Date().toISOString().split('T')[0],
      monthlyMinimumOrder: Number(form.monthlyMinimumOrder) || 0,
      monthlyMinimumOrderAmountPaid: Number(form.monthlyMinimumOrderAmountPaid) || 0,
      monthlyCopiesOrdered: Number(form.monthlyCopiesOrdered) || 0,
      cumulativeSponsoredAmountPaid: Number(form.cumulativeSponsoredAmountPaid) || 0,
      proofOfPayment: form.proofOfPaymentFiles.length,
      praiseReports: form.praiseReports,
      datesReceived: form.datesReceived,
    });
    setSubmitting(false);
  };

  const totalOrdered = (Number(form.adultCopies) || 0) + (Number(form.teensCopies) || 0) + (Number(form.kidsCopies) || 0);

  return (
    <FormContainer title="Weekly Magazine Order Report" eyebrow="KingsForms · Magazine" icon="📚"
      onSubmit={handleSubmit} submitting={submitting} submitLabel="Submit Magazine Report">

      <div className="popup-fields">
        <Field label="Date">
          <input className="kf-input" type="date"
            value={form.sponsorshipDate} onChange={e => setForm(p => ({ ...p, sponsorshipDate: e.target.value }))} />
        </Field>
        <Field label="State the Amount Paid This Week">
          <input className="kf-input" type="number" min="0" placeholder="0.00" step="0.01"
            value={form.amountPaidThisWeek} onChange={e => setForm(p => ({ ...p, amountPaidThisWeek: e.target.value }))} />
        </Field>
      </div>

      {/* Magazine Types */}
      <div className="popup-section-head">📚 Monthly Minimum Magazine Order (Please State Number of Magazines Ordered for Each Category - Adult, Teens, Kids)</div>
      <div className="popup-fields">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <Field label="Adult Copies" error={errors.adultCopies}>
            <input className="kf-input" type="number" min="0" placeholder="0"
              value={form.adultCopies} onChange={e => setForm(p => ({ ...p, adultCopies: e.target.value }))} />
          </Field>
          <Field label="Adult Language(s)" error={errors.adultLanguages}>
            <div className="kf-select-wrap">
              <select className="kf-select" value={form.adultLanguages} onChange={e => setForm(p => ({ ...p, adultLanguages: e.target.value }))}>
                <option value="">Select Language</option>
                <option value="English">English</option><option value="French">French</option><option value="Spanish">Spanish</option><option value="Portuguese">Portuguese</option>
                <option value="German">German</option><option value="Italian">Italian</option><option value="Russian">Russian</option><option value="Chinese">Chinese</option>
                <option value="Japanese">Japanese</option><option value="Korean">Korean</option><option value="Arabic">Arabic</option><option value="Hindi">Hindi</option>
                <option value="Bengali">Bengali</option><option value="Urdu">Urdu</option><option value="Indonesian">Indonesian</option><option value="Swahili">Swahili</option>
                <option value="Dutch">Dutch</option><option value="Turkish">Turkish</option><option value="Vietnamese">Vietnamese</option><option value="Polish">Polish</option>
                <option value="Yoruba">Yoruba</option><option value="Igbo">Igbo</option><option value="Hausa">Hausa</option><option value="Other">Other</option>
              </select>
              <ChevronDown className="kf-select-chevron" size={16} />
            </div>
            {form.adultLanguages === 'Other' && (
              <input className="kf-input" style={{ marginTop: '0.5rem' }} placeholder="Specify Language..."
                value={form.adultLanguagesOther} onChange={e => setForm(p => ({ ...p, adultLanguagesOther: e.target.value }))} />
            )}
          </Field>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
          <Field label="Teens Copies" error={errors.teensCopies}>
            <input className="kf-input" type="number" min="0" placeholder="0"
              value={form.teensCopies} onChange={e => setForm(p => ({ ...p, teensCopies: e.target.value }))} />
          </Field>
          <Field label="Teens Language(s)" error={errors.teensLanguages}>
            <div className="kf-select-wrap">
              <select className="kf-select" value={form.teensLanguages} onChange={e => setForm(p => ({ ...p, teensLanguages: e.target.value }))}>
                <option value="">Select Language</option>
                <option value="English">English</option><option value="French">French</option><option value="Spanish">Spanish</option><option value="Portuguese">Portuguese</option>
                <option value="German">German</option><option value="Italian">Italian</option><option value="Russian">Russian</option><option value="Chinese">Chinese</option>
                <option value="Japanese">Japanese</option><option value="Korean">Korean</option><option value="Arabic">Arabic</option><option value="Hindi">Hindi</option>
                <option value="Bengali">Bengali</option><option value="Urdu">Urdu</option><option value="Indonesian">Indonesian</option><option value="Swahili">Swahili</option>
                <option value="Dutch">Dutch</option><option value="Turkish">Turkish</option><option value="Vietnamese">Vietnamese</option><option value="Polish">Polish</option>
                <option value="Yoruba">Yoruba</option><option value="Igbo">Igbo</option><option value="Hausa">Hausa</option><option value="Other">Other</option>
              </select>
              <ChevronDown className="kf-select-chevron" size={16} />
            </div>
            {form.teensLanguages === 'Other' && (
              <input className="kf-input" style={{ marginTop: '0.5rem' }} placeholder="Specify Language..."
                value={form.teensLanguagesOther} onChange={e => setForm(p => ({ ...p, teensLanguagesOther: e.target.value }))} />
            )}
          </Field>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
          <Field label="Kids Copies" error={errors.kidsCopies}>
            <input className="kf-input" type="number" min="0" placeholder="0"
              value={form.kidsCopies} onChange={e => setForm(p => ({ ...p, kidsCopies: e.target.value }))} />
          </Field>
          <Field label="Kids Language(s)" error={errors.kidsLanguages}>
            <div className="kf-select-wrap">
              <select className="kf-select" value={form.kidsLanguages} onChange={e => setForm(p => ({ ...p, kidsLanguages: e.target.value }))}>
                <option value="">Select Language</option>
                <option value="English">English</option><option value="French">French</option><option value="Spanish">Spanish</option><option value="Portuguese">Portuguese</option>
                <option value="German">German</option><option value="Italian">Italian</option><option value="Russian">Russian</option><option value="Chinese">Chinese</option>
                <option value="Japanese">Japanese</option><option value="Korean">Korean</option><option value="Arabic">Arabic</option><option value="Hindi">Hindi</option>
                <option value="Bengali">Bengali</option><option value="Urdu">Urdu</option><option value="Indonesian">Indonesian</option><option value="Swahili">Swahili</option>
                <option value="Dutch">Dutch</option><option value="Turkish">Turkish</option><option value="Vietnamese">Vietnamese</option><option value="Polish">Polish</option>
                <option value="Yoruba">Yoruba</option><option value="Igbo">Igbo</option><option value="Hausa">Hausa</option><option value="Other">Other</option>
              </select>
              <ChevronDown className="kf-select-chevron" size={16} />
            </div>
            {form.kidsLanguages === 'Other' && (
              <input className="kf-input" style={{ marginTop: '0.5rem' }} placeholder="Specify Language..."
                value={form.kidsLanguagesOther} onChange={e => setForm(p => ({ ...p, kidsLanguagesOther: e.target.value }))} />
            )}
          </Field>
        </div>
      </div>

      {/* Receipt Status */}
      <div className="popup-section-head">📬 Receipt Status</div>
      <div className="popup-fields">
        <Field label="Number of Copies Received" required error={errors.receivedCopies}>
          <input className={`kf-input${errors.receivedCopies ? ' kf-input-err' : ''}`} type="number" min="0" placeholder="0"
            value={form.receivedCopies} onChange={e => setForm(p => ({ ...p, receivedCopies: e.target.value }))} />
        </Field>

        {Number(form.receivedCopies) < totalOrdered && (
          <Field label="If Not Received — Provide Reason" required error={errors.notReceivedReason}>
            <textarea className={`kf-textarea${errors.notReceivedReason ? ' kf-input-err' : ''}`} rows={3}
              placeholder="Explain Why Copies Were Not Received…"
              value={form.notReceivedReason} onChange={e => setForm(p => ({ ...p, notReceivedReason: e.target.value }))} />
          </Field>
        )}
        <Field label="State Challenges Faced or Complaints">
          <textarea className="kf-textarea" rows={3} placeholder="Describe Any Challenges Faced..."
            value={form.challengesFaced} onChange={e => setForm(p => ({ ...p, challengesFaced: e.target.value }))} />
        </Field>
      </div>

      {/* Monthly Ordering */}
      <div className="popup-section-head">📦 Monthly Sponsorship</div>
      <div className="popup-fields">
          <Field label="How Much Was Given Towards Magazine Sponsorship?">
            <input className="kf-input" type="number" min="0" placeholder="0.00" step="0.01"
              value={form.cumulativeSponsoredAmountPaid} onChange={e => setForm(p => ({ ...p, cumulativeSponsoredAmountPaid: e.target.value }))} />
          </Field>
      </div>

      {/* POP */}
      <div className="popup-section-head">🧾 Proof of Payment (POP)</div>
      <div className="popup-fields">
        <MediaUploader
          files={form.proofOfPaymentFiles}
          onAdd={files => {
            const prev = files.map(f => ({ name: f.name, type: f.type, url: (f.type.startsWith('image') || f.type.startsWith('video')) ? URL.createObjectURL(f) : null, size: f.size }));
            setForm(p => ({ ...p, proofOfPaymentFiles: [...p.proofOfPaymentFiles, ...prev] }));
          }}
          onRemove={i => setForm(p => ({ ...p, proofOfPaymentFiles: p.proofOfPaymentFiles.filter((_, j) => j !== i) }))}
          label="Upload Proof of Payment (POP)"
          accept="image/*,.pdf,.doc,.docx"
        />
      </div>

      {/* Praise Reports */}
      <div className="popup-section-head">📝 Praise Reports</div>
      <div className="popup-fields">
        <Field label="Praise Reports from Outreaches This Week">
          <textarea className="kf-textarea" rows={2} placeholder="Share Praise Reports from the Magazine Distribution…"
            value={form.praiseReports} onChange={e => setForm(p => ({ ...p, praiseReports: e.target.value }))} />
        </Field>
      </div>
    </FormContainer>
  );
}


// ══════════════════════════════════════════════════════
// OUTREACH REPORT FORM
// ══════════════════════════════════════════════════════
const OUTREACH_CATEGORIES = ['Healing', 'Soul-winning', 'Prison Outreach', 'School Outreach', 'Market Outreach', 'Hospital Outreach', 'Other'];

function OutreachForm({ onClose, onSubmit: parentSubmit }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    date: '',
    category: '',
    customCategory: '',
    locations: '',
    story: '',
    images: [],
    magazinesUsed: '',
    peopleInvolved: '',
    totalAttendance: '',
    soulsSaved: '',
    outreachTestimonies: '',
    followUpPlan: '',
    testimonyFiles: [],
    healingTranslationsAchieved: '',
    healingOutreachesHeld: '',
    healingOutreachLocations: '',
    healingMediaSubmitted: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const e = {};
    if (!form.date) e.date = 'Required';
    if (!form.category) e.category = 'Please select a category';
    if (!form.locations.trim()) e.locations = 'Required';
    setErrors(e);
    if (Object.keys(e).length) return;
    setSubmitting(true);
    await new Promise(r => setTimeout(r, 500));
    // Payload matches outreach_reports table columns
    parentSubmit({
      submittedBy: user?.displayName || user?.name || 'User',
      submittedDate: form.date,
      category: form.category === 'Other' ? form.customCategory || 'Other' : form.category,
      locations: form.locations,
      story: form.story,
      mediaCount: form.images.length,
      status: 'PENDING',
      rawDate: form.date,
      httnMagazineTestimoniesOutreaches: '',
      magazinesUsed: parseInt(form.magazinesUsed) || 0,
      peopleInvolved: parseInt(form.peopleInvolved) || 0,
      totalAttendance: parseInt(form.totalAttendance) || 0,
      soulsSaved: parseInt(form.soulsSaved) || 0,
      outreachTestimonies: (() => {
        let finalTestimonies = form.outreachTestimonies;
        if (form.testimonyFiles && form.testimonyFiles.length > 0) {
          const filesList = form.testimonyFiles.map(f => f.name).join(', ');
          finalTestimonies = [
            form.outreachTestimonies,
            `Attachments: ${filesList}`
          ].filter(Boolean).join('\n\n');
        }
        return finalTestimonies;
      })(),
      followUpPlan: form.followUpPlan,
      healingTranslationsAchieved: Number(form.healingTranslationsAchieved) || 0,
      healingOutreachesHeld: Number(form.healingOutreachesHeld) || 0,
      healingOutreachLocations: form.healingOutreachLocations,
      healingMediaSubmitted: Number(form.healingMediaSubmitted) || 0,
    });
    setSubmitting(false);
  };

  return (
    <FormContainer title="Weekly Outreach/Crusade Activity Report" eyebrow="KingsForms · Outreach" icon="📍"
      onSubmit={handleSubmit} submitting={submitting} submitLabel="Submit Outreach/Crusade Report">

      {/* Date */}
      <div className="popup-fields" style={{ marginBottom: '16px' }}>
        <Field label="Date of Outreach/Crusade" required error={errors.date}>
          <input className={`kf-input${errors.date ? ' kf-input-err' : ''}`} type="date"
            value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} />
        </Field>
      </div>

      {/* Category */}
      <div className="popup-section-head">🏷️ Outreach/Crusade Category</div>
      <div className="popup-fields">
        <Field label="What Type of Outreach/Crusade Was This?" required hint="(Healing, Soul-winning, Etc.)" error={errors.category}>
          <div className="outreach-cat-grid">
            {OUTREACH_CATEGORIES.map(cat => (
              <button key={cat} type="button"
                className={`toggle-btn outreach-cat-btn ${form.category === cat ? 'selected' : ''}`}
                onClick={() => setForm(p => ({ ...p, category: cat }))}>
                {cat}
              </button>
            ))}
          </div>
        </Field>
        {form.category === 'Other' && (
          <Field label="Please Specify Type">
            <input className="kf-input" placeholder="Enter Category…"
              value={form.customCategory} onChange={e => setForm(p => ({ ...p, customCategory: e.target.value }))} />
          </Field>
        )}
      </div>

      {/* Outreach Details */}
      <div className="popup-section-head">📍 Outreach/Crusade Details</div>
      <div className="popup-fields">
        <Field label="State the Location of Healing Outreaches (E.g. Market, Hospitals, School, Etc.)" required hint="Separate Multiple Locations With Commas" error={errors.locations}>
          <input className={`kf-input${errors.locations ? ' kf-input-err' : ''}`}
            placeholder="E.g. Lagos Island, Surulere Market"
            value={form.locations} onChange={e => setForm(p => ({ ...p, locations: e.target.value }))} />
        </Field>

        <Field label="How Many Copies of Magazine Were Distributed?">
          <input className="kf-input" type="number" min="0" placeholder="0"
            value={form.magazinesUsed} onChange={e => setForm(p => ({ ...p, magazinesUsed: e.target.value }))} />
        </Field>

        <Field label="How Many Healing Outreach(es) Were Carried Out?">
          <input className="kf-input" type="number" min="0" placeholder="0"
            value={form.healingOutreachesHeld} onChange={e => setForm(p => ({ ...p, healingOutreachesHeld: e.target.value }))} />
        </Field>

        <Field label="How Many Souls Were Saved?">
          <input className="kf-input" type="number" min="0" placeholder="0"
            value={form.soulsSaved} onChange={e => setForm(p => ({ ...p, soulsSaved: e.target.value }))} />
        </Field>

        <Field label="Kindly Submit Testimonies From the Outreach(es)/Crusade(s)">
          <textarea className="kf-textarea" rows={3} placeholder="Give Testimonies..."
            value={form.outreachTestimonies} onChange={e => setForm(p => ({ ...p, outreachTestimonies: e.target.value }))} />
        </Field>

        <MediaUploader files={form.images}
          onAdd={files => {
            const prev = files.map(f => ({ name: f.name, type: f.type, url: (f.type.startsWith('image') || f.type.startsWith('video')) ? URL.createObjectURL(f) : null, size: f.size }));
            setForm(p => ({ ...p, images: [...p.images, ...prev] }));
          }}
          onRemove={i => setForm(p => ({ ...p, images: p.images.filter((_, j) => j !== i) }))}
          label="Upload Photos & Videos (Includes Healing Outreaches)"
          accept="image/*,video/*"
        />

        <Field label="Kindly State Further Plans for Soul Retention">
          <textarea className="kf-textarea" rows={3} placeholder="State Follow-up Plan..."
            value={form.followUpPlan} onChange={e => setForm(p => ({ ...p, followUpPlan: e.target.value }))} />
        </Field>
      </div>
    </FormContainer>
  );
}


// ══════════════════════════════════════════════════════
// TABS CONFIG
// ══════════════════════════════════════════════════════
const TABS_CONFIG = [
  {
    id: 'zonal-info', label: 'Zonal Information', icon: <Users size={14} />, emoji: '🏛️', color: '#8b5cf6',
    columns: [
      { key: 'id', label: 'Report ID' }, { key: 'rawDate', label: 'Date' },
      { key: 'zoneName', label: 'Zone' }, { key: 'zonalPastor', label: "Zonal Pastor's Name" }, { key: 'zonalManager', label: 'Zonal Manager' },
      { key: 'status', label: 'Status' },
    ],
    FormComponent: ZonalInformationForm, btnLabel: 'Upload New Zonal Information',
  },
  {
    id: 'zonal', label: 'Programs/Campaign Engagement Report', icon: <Users size={14} />, emoji: '📊', color: '#4f46e5',
    columns: [
      { key: 'id', label: 'Report ID' }, { key: 'rawDate', label: 'Date' },
      { key: 'submittedBy', label: 'Submitted By' },
      { key: 'partners', label: 'New Partners' },
      { key: 'totalRegistrationHslhs', label: 'HSLHS Reg' },
      { key: 'status', label: 'Status' },
    ],
    FormComponent: ZonalReportForm, btnLabel: 'Upload a New Programs/Campaign Engagement Report',
  },
  {
    id: 'partnership', label: 'Partnership Report', icon: <Heart size={14} />, emoji: '🤝', color: '#16a34a',
    columns: [
      { key: 'id', label: 'Report ID' }, { key: 'rawDate', label: 'Date' },
      { key: 'zonalPartnership', label: 'Zonal Partnership' },
      { key: 'groupsPartnership', label: 'Groups' },
      { key: 'churchesPartnership', label: 'Churches' },
      { key: 'cellPartnership', label: 'Cell' },
      { key: 'groupPastorsMilestones', label: 'BLAAAST Milestones' },
      { key: 'sponsoredTeenspiration', label: 'Teenspiration (300 espees)' },
      { key: 'sponsoredKidspiration', label: 'Kidspiration (300 espees)' },
      { key: 'status', label: 'Status' },
    ],
    FormComponent: PartnershipForm, btnLabel: 'Upload a New Partnership Report',
  },
  {
    id: 'testimonials', label: 'Testimonials', icon: <BookOpen size={14} />, emoji: '✍️', color: '#d97706',
    columns: [
      { key: 'id', label: 'Report ID' }, { key: 'rawDate', label: 'Date' },
      { key: 'memberName', label: 'Member Name' },
      { key: 'testimoniesCount', label: 'Count' },
      { key: 'prayWithMeTestimonies', label: 'Pray With Me' },
      { key: 'translationTestimonies', label: 'Translation' },
      { key: 'partnershipTestimonies', label: 'Partnership' },
      { key: 'salvationTestimonies', label: 'Salvation' },
      { key: 'healingTestimonies', label: 'Healing' },
      { key: 'othersTestimonies', label: 'Others' },
      { key: 'status', label: 'Status' },
    ],
    FormComponent: TestimonialsForm, btnLabel: 'Upload a New Testimony',
  },
  {
    id: 'magazine', label: 'Magazine', icon: <Newspaper size={14} />, emoji: '📚', color: '#dc2626',
    columns: [
      { key: 'id', label: 'Report ID' }, { key: 'rawDate', label: 'Date' },
      { key: 'language', label: 'Language' },
      { key: 'ordered', label: 'Ordered' },
      { key: 'received', label: 'Received' },
      { key: 'datesReceived', label: 'Dates Received' },
      { key: 'sponsoredCopies', label: 'Sponsored' },
      { key: 'monthlyMinimumOrder', label: 'Min Order' },
      { key: 'monthlyCopiesOrdered', label: 'Monthly Sponsored' },
      { key: 'healingOutreaches', label: 'Healing Outreaches' },
      { key: 'outreachLocations', label: 'Outreach Locations' },
      { key: 'status', label: 'Status' },
    ],
    FormComponent: MagazineForm, btnLabel: 'Upload a New Magazine Report',
  },
  {
    id: 'outreach', label: 'Outreach Report', icon: <FileText size={14} />, emoji: '📍', color: '#0891b2',
    columns: [
      { key: 'submittedBy', label: 'Submitted By' },
      { key: 'id', label: 'Report ID' }, { key: 'rawDate', label: 'Date' },
      { key: 'category', label: 'Category' },
      { key: 'locations', label: 'Location(s)' },
      { key: 'magazinesUsed', label: 'Magazines Used' },
      { key: 'peopleInvolved', label: 'Involved' },
      { key: 'totalAttendance', label: 'Attendance' },
      { key: 'soulsSaved', label: 'Saved' },
      { key: 'status', label: 'Status' },
    ],
    FormComponent: OutreachForm,
  },
  {
    id: 'submitted-forms', label: 'Submitted Forms', icon: <FolderOpen size={14} />, emoji: '📁', color: '#64748b'
  }
];

const downloadReportCSV = (report, formName) => {
  const skip = ['id', 'media', 'mediaFiles', 'rawDate', 'formData'];
  const entries = Object.entries(report).filter(([k, v]) => !skip.includes(k) && v !== undefined && v !== null && v !== '');
  
  const lines = [`"Report ID","${report.id}"`, `"Form","${formName}"`, ""];
  entries.forEach(([k, v]) => {
    const label = KEY_LABELS[k] || k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
    const safeLabel = `"${String(label).replace(/"/g, '""')}"`;
    const safeValue = `"${String(v).replace(/"/g, '""')}"`;
    lines.push(`${safeLabel},${safeValue}`);
  });

  const content = lines.join('\n');
  const a = Object.assign(document.createElement('a'), {
    href: URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8;' })),
    download: `${report.id}_${formName.replace(/\s+/g, '_')}.csv`,
  });
  a.click();
};

// ══════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════
export default function ReportingPortal() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('zonal-info');
  const [subTab, setSubTab] = useState('zonal-info');
  const [search, setSearch] = useState('');
  const [reportsByTab, setReportsByTab] = useState({ 'zonal-info': [], zonal: [], partnership: [], testimonials: [], magazine: [], outreach: [] });
  const [viewReport, setViewReport] = useState(null);
  const [clarifyReport, setClarifyReport] = useState(null);
  const [toast, setToast] = useState('');
  const [downloadOptionsReport, setDownloadOptionsReport] = useState(null);


  useEffect(() => {
    if (user) fetchAllReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchAllReports = async () => {
    const baseUrl = getApiUrl();
    const emailParam = (user?.role === 'admin' || user?.role === 'global') ? '' : `?email=${user?.email}`;

    try {
      const zRes = await fetch(`${baseUrl}/api/reports${emailParam}`);
      if (!zRes.ok) throw new Error(`ZR Fetch Error: ${zRes.status}`);
      const zData = await zRes.json();

      // Fetch others
      const tabs = ['partnership', 'testimonials', 'magazine', 'outreach'];
      const otherData = {};
      for (const t of tabs) {
        const res = await fetch(`${baseUrl}/api/portal-reports/${t}${emailParam}`);
        if (!res.ok) {
          console.warn(`Tab ${t} fetch failed with status ${res.status}`);
          otherData[t] = [];
          continue;
        }
        otherData[t] = await res.json();
      }

      setReportsByTab({
        'zonal-info': zData.map(r => ({
          ...r,
          id: `ZI-${String(r.id).padStart(3, '0')}`,
          rawDate: r.createdAt || r.submittedAt || r.submittedDate,
          submittedBy: r.submittedBy || r.submittedByEmail || r.zonalManager || 'User',
          zoneName: r.zoneName || '—',
          zonalPastor: r.zonalPastor || '—',
          zonalManager: r.zonalManager || '—',
          status: r.status
        })),
        zonal: zData.map(r => ({
          ...r,
          id: `ZR-${String(r.id).padStart(3, '0')}`,
          rawDate: r.createdAt || r.submittedAt || r.submittedDate,
          submittedBy: r.submittedBy || r.submittedByEmail || r.zonalManager || 'User',
          zone: r.zoneName || '—',

          partners: r.newPartnersRecruited,
          status: r.status
        })),
        partnership: otherData.partnership.map(r => ({ ...r, id: `PR-${String(r.id).padStart(3, '0')}`, rawDate: r.submittedDate })),
        testimonials: otherData.testimonials.map(r => ({ ...r, id: `TS-${String(r.id).padStart(3, '0')}`, rawDate: r.submittedDate })),
        magazine: otherData.magazine.map(r => ({
          ...r,
          id: `MG-${String(r.id).padStart(3, '0')}`,
          rawDate: r.submittedDate,
          magazineType: [r.isAdult && 'Adult', r.isTeevolution && 'Teevolution', r.isKidsMagazine && 'Kids'].filter(Boolean).join(', ') || 'N/A'
        })),
        outreach: otherData.outreach.map(r => ({ ...r, id: `OR-${String(r.id).padStart(3, '0')}`, rawDate: r.submittedDate })),
      });
    } catch (err) {
      console.error("Failed to fetch reports", err);
    }
  };

  const handleApprove = async (report) => {
    const baseUrl = getApiUrl();
    const endpoint = (subTab === 'zonal' || subTab === 'zonal-info') ? '/api/reports' : `/api/portal-reports/${subTab}`;
    const id = parseInt(String(report.id).replace(/^[A-Z]+-/, ''), 10);

    try {
      const res = await fetch(`${baseUrl}${endpoint}/${id}/approve`, { method: 'POST' });
      if (res.ok) {
        setToast('Report approved ✅');
        fetchAllReports();
      }
    } catch (e) {
      console.error(e);
      setToast('Failed to approve ❌');
    }
  };

  const handleDelete = async (report) => {
    if (!window.confirm('Delete this report? This cannot be undone.')) return;
    const baseUrl = getApiUrl();
    const endpoint = (subTab === 'zonal' || subTab === 'zonal-info') ? '/api/reports' : `/api/portal-reports/${subTab}`;
    const id = parseInt(String(report.id).replace(/^[A-Z]+-/, ''), 10);

    try {
      const res = await fetch(`${baseUrl}${endpoint}/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setToast('Report deleted 🗑️');
        fetchAllReports();
      } else {
        const errText = await res.text();
        setToast(`Delete failed: ${res.status} ${errText}`.substring(0, 100));
      }
    } catch (e) {
      console.error(e);
      setToast('Delete failed ❌');
    }
  };

  const tab = TABS_CONFIG.find(t => t.id === activeTab);
  const reports = reportsByTab[subTab] || reportsByTab[activeTab] || [];

  const handleSubmit = async (data) => {
    try {
      if (['partnership', 'testimonials', 'magazine', 'outreach'].includes(activeTab)) {
        const payload = {
          ...data,
          submittedBy: data.submittedBy || user?.displayName || user?.name || 'User',
          submitterEmail: user?.email || 'admin@loveworld.com',
          zoneName: data.zoneName || user?.zone || user?.region || 'Default Zone',
          status: 'PENDING',
        };
        const res = await fetch(`${getApiUrl()}/api/portal-reports/${activeTab}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const txt = await res.text();
          throw new Error(`Submit failed (${res.status}): ${txt}`);
        }
      }

      setToast(`${tab?.label || 'Report'} submitted successfully`);
      
      // Switch tabs instantly to submitted-forms
      setSubTab(activeTab);
      setActiveTab('submitted-forms');

      // Fetch fresh data in background
      await fetchAllReports();
    } catch (e) {
      console.error('Submission failed', e);
      alert(`Failed to save report: ${e.message}`);
    }
  };

  const handleExportAll = () => {
    setToast('Generating Excel file...');
    const wb = XLSX.utils.book_new();
    
    TABS_CONFIG.forEach(t => {
      const tabReports = reportsByTab[t.id] || [];
      if (tabReports.length === 0) return;
      
      const skip = ['media', 'mediaFiles', 'formData'];
      const allKeys = new Set();
      tabReports.forEach(r => {
        Object.keys(r).forEach(k => {
          if (!skip.includes(k) && r[k] !== undefined && r[k] !== null && r[k] !== '') {
            allKeys.add(k);
          }
        });
      });
      
      const keysArray = Array.from(allKeys).sort((a, b) => {
        const priority = ['id', 'rawDate', 'status'];
        const aIdx = priority.indexOf(a);
        const bIdx = priority.indexOf(b);
        if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
        if (aIdx !== -1) return -1;
        if (bIdx !== -1) return 1;
        return 0;
      });
      
      const headers = keysArray.map(k => KEY_LABELS[k] || k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()));
      const data = [headers];
      
      tabReports.forEach(r => {
        const row = keysArray.map(k => r[k] !== undefined ? String(r[k]) : '—');
        data.push(row);
      });
      
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, t.label.substring(0, 31));
    });
    
    if (wb.SheetNames.length === 0) {
      setToast('No reports to export');
      return;
    }
    
    const userName = user?.name ? user.name.replace(/\s+/g, '_') : 'User';
    XLSX.writeFile(wb, `${userName}_All_Reports.xlsx`);
    setToast('Exported all reports successfully');
  };

  return (
    <div className="rp-page">
      {toast && <Toast message={toast} onDone={() => setToast('')} />}
      {viewReport && <ViewModal report={viewReport} onClose={() => setViewReport(null)} />}
      {clarifyReport && (
        <ClarificationModal
          report={clarifyReport}
          endpoint={activeTab === 'zonal' ? '/api/reports' : `/api/portal-reports/${activeTab}`}
          onClose={() => setClarifyReport(null)}
          onDone={() => { setClarifyReport(null); setToast('Clarification request sent 💬'); fetchAllReports(); }}
        />
      )}

      {downloadOptionsReport && (
        <div className="rp-modal-overlay" onClick={() => setDownloadOptionsReport(null)}>
          <div className="rp-modal" style={{ maxWidth: 320 }} onClick={e => e.stopPropagation()}>
            <div className="rp-modal-header">
              <h3>Download Report</h3>
              <button className="rp-remove-btn" onClick={() => setDownloadOptionsReport(null)}><X size={16} /></button>
            </div>
            <div className="rp-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button className="submit-report-btn" style={{ '--btn-color': '#e11d48', width: '100%', justifyContent: 'center' }} onClick={() => {
                const fullReport = reports.find(x => x.id === downloadOptionsReport.id);
                if (fullReport) downloadReportPDF({ ...fullReport, formName: tab.label }, formatDate);
                setDownloadOptionsReport(null);
              }}>Download as PDF</button>
              
              <button className="submit-report-btn" style={{ '--btn-color': '#16a34a', width: '100%', justifyContent: 'center' }} onClick={() => {
                const fullReport = reports.find(x => x.id === downloadOptionsReport.id);
                if (fullReport) downloadReportCSV(fullReport, tab.label);
                setDownloadOptionsReport(null);
              }}>Download as CSV / Excel</button>
            </div>
          </div>
        </div>
      )}

      <div className="rp-page-header">
        <div>
          <h2>Reporting Portal {user?.role === 'admin' ? '— Admin View' : ''}</h2>
          <p>{user?.role === 'admin' ? 'Review, approve, and manage all submitted reports' : 'Submit, manage, and track weekly activity reports'}</p>
        </div>
        <button className="rp-export-btn excel" onClick={handleExportAll}><ExportIcon /> Download All (Excel)</button>
      </div>

      {/* ── Tabs ── */}
      <div className="rp-tabs">
        {TABS_CONFIG.map(t => {
          const count = t.id === 'submitted-forms'
            ? ((reportsByTab['zonal-info'] || []).length
              + (reportsByTab['partnership'] || []).length
              + (reportsByTab['testimonials'] || []).length
              + (reportsByTab['magazine'] || []).length
              + (reportsByTab['outreach'] || []).length)
            : (reportsByTab[t.id] || []).length;
          return (
            <button key={t.id}
              onClick={() => { setActiveTab(t.id); setSearch(''); }}
              className={`rp-tab ${activeTab === t.id ? 'active' : ''}`}
              style={activeTab === t.id ? { '--tab-color': t.color } : {}}>
              {t.icon} {t.label}
              <span style={{
                marginLeft: 8,
                background: activeTab === t.id ? 'rgba(255,255,255,0.3)' : (count > 0 ? '#4f46e5' : '#cbd5e1'),
                color: '#fff',
                padding: '2px 8px',
                borderRadius: 12,
                fontSize: 11,
                fontWeight: 700
              }}>
                {count}
              </span>
            </button>
          );
        })}
      </div>
      <div className="rp-tab-divider" />

      {/* ── Panel ── */}
      {activeTab === 'submitted-forms' ? (
        <div className="rp-panel">
          <div className="rp-toolbar">
            <div className="rp-search">
              <Search size={14} color="#9ca3af" />
              <input type="text" placeholder="Search submitted forms..."
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
          </div>

          <div className="rp-sub-tabs" style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
            {TABS_CONFIG.filter(t => t.id !== 'submitted-forms').map(t => (
              <button key={t.id}
                onClick={() => setSubTab(t.id)}
                style={{
                  padding: '6px 12px', borderRadius: '20px', border: '1px solid #e2e8f0',
                  background: subTab === t.id ? t.color : '#f8fafc',
                  color: subTab === t.id ? '#fff' : '#475569',
                  fontSize: '13px', cursor: 'pointer', fontWeight: 500,
                  transition: 'all 0.2s'
                }}>
                <span>{t.label}</span>
                <span style={{
                  marginLeft: 6,
                  background: subTab === t.id ? 'rgba(255,255,255,0.3)' : (((reportsByTab[t.id] || []).length > 0) ? '#4f46e5' : '#cbd5e1'),
                  color: '#fff',
                  padding: '1px 7px',
                  borderRadius: 10,
                  fontSize: 11,
                  fontWeight: 700
                }}>
                  {(reportsByTab[t.id] || []).length}
                </span>
              </button>
            ))}
          </div>

          <p className="rp-count">
             Showing {
               (reportsByTab[subTab] || []).filter(r => Object.values(r).some(v => String(v).toLowerCase().includes(search.toLowerCase()))).length
             } reports
          </p>

          <ReportTable
            reports={(reportsByTab[subTab] || [])
              .filter(r => Object.values(r).some(v => String(v).toLowerCase().includes(search.toLowerCase())))
              .map(r => ({ ...r, rawDate: formatDate(r.rawDate) }))}
            loading={false}
            columns={(() => {
               const st = TABS_CONFIG.find(t => t.id === subTab);
               if (!st) return [];
               return user?.role === 'admin'
                ? (st.columns.some(c => c.key === 'submittedBy') ? st.columns : [{ key: 'submittedBy', label: 'Submitted By' }, ...st.columns])
                : st.columns;
            })()}
            onView={r => setViewReport(r)}
            onDownload={r => setDownloadOptionsReport(r)}
            onApprove={handleApprove}
            onClarify={r => setClarifyReport(r)}
            onDelete={handleDelete}
            userRole={user?.role}
          />
        </div>
      ) : (
        <div className="rp-panel" style={{ padding: '0', background: 'transparent', boxShadow: 'none' }}>
           {tab && tab.FormComponent && (
             <tab.FormComponent
               onSubmit={handleSubmit}
             />
           )}
        </div>
      )}
    </div>
  );
}
